import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import {
  DtoFilterValues,
  DtoReferenceCategory,
  DtoReferencePageInfo,
  IFilterItemDto,
  Language,
  ReferenceCategoryType,
  ReferenceDocumentType
} from '@common';
import { DtoSelfServiceFormData, ReferencesService } from '../../../../core';
import { MarkdownPipe } from '../../../../shared';

@Component({
  selector: 'app-generation-step',
  imports: [CommonModule, MarkdownPipe, MatButtonModule, MatCheckboxModule, MatStepperModule, MatTableModule],
  templateUrl: './generation-step.component.html',
  styleUrls: ['./generation-step.component.scss', '../self-service-form.step.scss']
})
export class GenerationStepComponent implements OnInit, OnChanges {
  @Input({ required: true }) formData!: DtoSelfServiceFormData;
  @Input({ required: true }) language: Language;
  @Input({ required: true }) infoPage: ReferenceCategoryType | null;
  @Input({ required: true }) documentType: ReferenceDocumentType;
  @Input({ required: true }) supportedCategories!: Array<DtoReferenceCategory>;
  @Input({ required: true }) filterValues!: DtoFilterValues;
  @Input({ required: true }) brand!: string;

  //#region Private Fields ----------------------------------------------------
  private readonly referencesService: ReferencesService;
  //#endregion

  //#region Protected Fields --------------------------------------------------
  protected readonly displayedColumns: Array<string>;
  protected readonly selection: SelectionModel<DtoReferencePageInfo>;
  protected generatedPdfLink: string | null;
  protected pages: Array<DtoReferencePageInfo>;
  protected categoryLabels: Map<ReferenceCategoryType, string>;
  //#endregion

  //#region Getters-Setters ---------------------------------------------------
  protected get isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.pages.length;
    return numSelected == numRows;
  }
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  public constructor(referencesService: ReferencesService) {
    this.referencesService = referencesService;
    this.displayedColumns = new Array<string>('select', 'category', 'title', 'customer', 'role', 'keyword', 'period');
    this.selection = new SelectionModel<DtoReferencePageInfo>(true, new Array<DtoReferencePageInfo>());
    this.selection.changed.subscribe(() => {
      this.setGeneratedPdfLink();
    });
    this.pages = new Array<DtoReferencePageInfo>();
    this.generatedPdfLink = null;
  }

  public ngOnInit(): void {
    this.categoryLabels = new Map<ReferenceCategoryType, string>();
    this.supportedCategories.forEach((dto: DtoReferenceCategory) =>
      this.categoryLabels.set(dto.category, dto.shortLabel[this.language])
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterValues'] || changes['language']) {
      this.reloadTable();
    } else if (changes['documentType'] || changes['infoPage'] || changes['brand']) {
      this.setGeneratedPdfLink();
    }
  }
  //#endregion

  //#region UI-Triggers -------------------------------------------------------
  protected toggleAllRows(): void {
    if (this.isAllSelected) {
      this.selection.clear();
    } else {
      this.pages.forEach((row) => this.selection.select(row));
    }
  }
  //#endregion

  //#region Auxiliary Methods -------------------------------------------------
  private setGeneratedPdfLink(): void {
    this.generatedPdfLink = this.referencesService.buildPdfLink(
      this.language,
      this.infoPage,
      this.brand,
      this.documentType,
      this.selection.selected.map((dto: DtoReferencePageInfo) => dto.id)
    );
  }

  private reloadTable(): void {
    this.referencesService
      .fetchAvailablePages(
        this.language,
        this.displayFilterToFetchFilter(this.filterValues.categories) as Array<ReferenceCategoryType>,
        this.displayFilterToFetchFilter(this.filterValues.customers),
        this.displayFilterToFetchFilter(this.filterValues.roles),
        this.displayFilterToFetchFilter(this.filterValues.keywords)
      )
      .subscribe((pages: Array<DtoReferencePageInfo>) => {
        this.pages = pages;
        this.selection.select(...this.pages);
      });
  }

  private displayFilterToFetchFilter(filterValues: Array<IFilterItemDto>): Array<string> | undefined {
    return filterValues.length > 0 ? filterValues.map((v: IFilterItemDto) => v.key) : undefined;
  }
  //#endregion
}
